import { useCallback, useMemo, useReducer } from 'react';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import {
  TimetableVersionResourceType,
  useAddTimetableVersionResourcesMutation,
  useLessonsForTimetableVersionQuery,
  useRemoveTimetableVersionResourcesMutation,
} from '../../../types/planung-graphql-client-defs';

export const useTimetableClasses = (versionUuid: string) => {
  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const [, addResources] = useAddTimetableVersionResourcesMutation();
  const [, removeResources] = useRemoveTimetableVersionResourcesMutation();

  const [{ data: lessonsData }] = useLessonsForTimetableVersionQuery({
    variables: { versionUuid },
  });

  const importExportCache = useMemo(() => {
    return {
      additionalTypenames: ['TimetableVersion'],
    };
  }, []);

  const importClasses = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    if (uuids.length > 0) {
      await addResources({ versionUuid, resourceType: TimetableVersionResourceType.Class, uuids }, importExportCache);
    }
    dispatch({ type: 'SET_LOADING', uuids: uuids });
  };

  const canBeRemoved = (uuids: string[]) => {
    const lesson = lessonsData?.lessonsForTimetableVersion.find((l) => {
      const classes = l.lessonClasses.map((lessonClass) => {
        return lessonClass.class.uuid;
      });
      return classes.some((c) => uuids.includes(c));
    });
    return !lesson;
  };

  const removeClasses = useCallback(
    async (uuids: string[]) => {
      dispatch({ type: 'SET_LOADING', uuids: uuids });
      if (uuids.length > 0) {
        await removeResources(
          { versionUuid, resourceType: TimetableVersionResourceType.Class, uuids },
          importExportCache,
        );
      }
      dispatch({ type: 'SET_LOADING', uuids: uuids });
    },
    [importExportCache, removeResources, versionUuid],
  );

  return {
    importClasses,
    canBeRemoved,
    removeClasses,
    loadingState,
  };
};
